// =====================================================================
// src/screens/v2/MerchantChatList.jsx
//
// I2a (Jun 16): merchant-side chat inbox.
//
// Renders when the header chat icon on MerchantApp is tapped. Lists the
// merchant's conversations grouped by CUSTOMER (one row per unique
// customer, surfaced via their most recent order). Tapping a row opens
// the underlying order chat — the existing /v2=order-chat&id=ORDERID
// screen which already works on both customer and merchant sides.
//
// Design notes:
//   - Receives `orders` (UI-shaped, already filtered to active business
//     by MerchantApp). Zero new Firestore reads — fully derived data.
//   - Group-by-customer matches the prototype's Variant 1 inbox; avoids
//     duplicate rows when one customer has placed multiple orders.
//   - Empty state: shown when no orders exist for the active business.
//   - Active/Requests tabs are deliberately omitted in this V1 —
//     'Active' meaningfully describes any in-flight chat for a merchant,
//     and 'Requests' doesn't map to any current order state. Re-add
//     when there's clearer UX semantics (e.g. unread-only filter).
// =====================================================================

import { ArrowLeft, MessageSquare } from 'lucide-react';
import './ConnectList.css'; // reuse base styles — visual parity with the
                            // V1-prototype's chat list

// Statuses that still need merchant attention. Used to badge rows so
// "I have 2 chats" doesn't surface fully-delivered conversations
// mixed in with active ones.
const ACTIVE_STATUSES = ['placed', 'confirmed', 'preparing', 'ready', 'out_for_delivery'];

export default function MerchantChatList({ orders, onBack, onOpenChat }) {
  // Dedupe by customerId. Input `orders` is sorted newest-first by
  // MerchantApp's normalizer (relativeTime + placedAt ordering), so
  // keeping the first occurrence per customerId yields the latest order
  // per customer — which is what the row's badge + status reflect.
  const conversations = (() => {
    const seen = new Set();
    const rows = [];
    for (const o of orders) {
      if (!o.customerId) continue;
      if (seen.has(o.customerId)) continue;
      seen.add(o.customerId);
      rows.push(o);
    }
    return rows;
  })();

  return (
    <div className="v2-connect-list">
      {/* Top bar — back + title */}
      <div className="v2cl-top-bar" style={{ flexShrink: 0 }}>
        <button className="v2cl-back-btn" onClick={onBack} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <div className="v2cl-top-info">
          <h1 className="v2cl-top-title">Chats</h1>
          <p className="v2cl-top-subtitle">
            {conversations.length === 0
              ? 'No conversations yet'
              : `${conversations.length} conversation${conversations.length === 1 ? '' : 's'}`}
          </p>
        </div>
      </div>

      {/* Content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        padding: '8px 16px 24px',
      }}>
        {conversations.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '48px 16px',
            color: 'var(--ink-soft, #6E6F84)',
          }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>💬</div>
            <h2 style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: 18,
              color: 'var(--ink, #2A2418)',
              marginBottom: 6,
            }}>
              No conversations yet
            </h2>
            <p style={{ fontSize: 13, lineHeight: 1.5 }}>
              When customers place orders with you, their chats will appear here.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {conversations.map(o => {
              const isActive = ACTIVE_STATUSES.includes(o.status);
              const secondary = secondaryLine(o);
              return (
                <button
                  key={o.customerId}
                  onClick={() => onOpenChat?.(o.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px',
                    background: 'var(--surface, #FFFCF6)',
                    border: '1px solid var(--border, #E8DDC6)',
                    borderRadius: 14,
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                    fontFamily: 'inherit',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {/* Avatar */}
                  <div style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    background: o.customerColor || '#C45A3F',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: 15,
                    flexShrink: 0,
                  }}>
                    {o.customerInitial || '?'}
                  </div>

                  {/* Body */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 8,
                      marginBottom: 2,
                    }}>
                      <span style={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: 'var(--ink, #2A2418)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                      }}>
                        {o.customerName}
                      </span>
                      <span style={{
                        fontSize: 11,
                        color: 'var(--ink-soft, #6E6F84)',
                        flexShrink: 0,
                      }}>
                        {o.placedAt}
                      </span>
                    </div>
                    <div style={{
                      fontSize: 12,
                      color: 'var(--ink-soft, #6E6F84)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {secondary}
                    </div>
                  </div>

                  {/* Right indicator */}
                  {isActive && (
                    <div style={{
                      flexShrink: 0,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'var(--accent, #C45A3F)',
                    }} />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Human-readable secondary line for each row: surfaces order status +
// short ID. Eventually this could be the last chat message preview,
// but that requires a per-chat read which we're deferring to keep
// Firestore costs predictable (one chat-list open = ~0 extra reads
// today vs. N reads with previews).
function secondaryLine(order) {
  const status = order.status || 'placed';
  const label = STATUS_LABEL[status] || status;
  const id = order.displayNumber || order.id;
  return `${label} · #${id}`;
}

const STATUS_LABEL = {
  placed: 'Order placed — needs review',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready: 'Ready',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
};
